import { produce } from 'immer';
import type { BulkAttributeState, BulkAttributeValue } from './BulkUpdateInputRenderer';

export type CollapsedGroupState = { groupId: number; collapsed: boolean };

export type BulkUpdateDialogState = {
  search: string;
  attributesState: Record<string, BulkAttributeState>;
  collapsedGroups: CollapsedGroupState[];
  pendingUpdates: Array<{ attrInternalName: string; value: BulkAttributeValue }>;
  isConfirmOpen: boolean;
  isSubmitting: boolean;
  hasAttemptedSave: boolean;
};

const Events = {
  RESET_DIALOG: 'RESET_DIALOG',
  SET_SEARCH: 'SET_SEARCH',
  UPSERT_ATTRIBUTE_STATE: 'UPSERT_ATTRIBUTE_STATE',
  REPLACE_ATTRIBUTE_STATES: 'REPLACE_ATTRIBUTE_STATES',
  PREPARE_CONFIRMATION: 'PREPARE_CONFIRMATION',
  TOGGLE_COLLAPSED_GROUP: 'TOGGLE_COLLAPSED_GROUP',
  SET_CONFIRM_OPEN: 'SET_CONFIRM_OPEN',
  SET_SUBMITTING: 'SET_SUBMITTING',
  SET_HAS_ATTEMPTED_SAVE: 'SET_HAS_ATTEMPTED_SAVE',
  CLEAR_ALL: 'CLEAR_ALL',
} as const;

type Events = (typeof Events)[keyof typeof Events];

type Action =
  | {
      type: typeof Events.RESET_DIALOG;
      attributesState: Record<string, BulkAttributeState>;
      collapsedGroups: CollapsedGroupState[];
    }
  | { type: typeof Events.SET_SEARCH; search: string }
  | {
      type: typeof Events.UPSERT_ATTRIBUTE_STATE;
      attrInternalName: string;
      attributeState: BulkAttributeState;
    }
  | {
      type: typeof Events.REPLACE_ATTRIBUTE_STATES;
      attributesState: Record<string, BulkAttributeState>;
    }
  | {
      type: typeof Events.PREPARE_CONFIRMATION;
      validatedStates: Record<string, BulkAttributeState>;
      updates: Array<{ attrInternalName: string; value: BulkAttributeValue }>;
    }
  | { type: typeof Events.TOGGLE_COLLAPSED_GROUP; groupId: number }
  | { type: typeof Events.SET_CONFIRM_OPEN; open: boolean }
  | { type: typeof Events.SET_SUBMITTING; isSubmitting: boolean }
  | { type: typeof Events.SET_HAS_ATTEMPTED_SAVE; hasAttemptedSave: boolean }
  | { type: typeof Events.CLEAR_ALL; attributesState: Record<string, BulkAttributeState> };

export const BulkUpdateDialogActions = {
  resetDialog: (payload: {
    attributesState: Record<string, BulkAttributeState>;
    collapsedGroups: CollapsedGroupState[];
  }): Action => ({
    type: Events.RESET_DIALOG,
    attributesState: payload.attributesState,
    collapsedGroups: payload.collapsedGroups,
  }),
  setSearch: (search: string): Action => ({ type: Events.SET_SEARCH, search }),
  upsertAttributeState: (payload: {
    attrInternalName: string;
    attributeState: BulkAttributeState;
  }): Action => ({
    type: Events.UPSERT_ATTRIBUTE_STATE,
    attrInternalName: payload.attrInternalName,
    attributeState: payload.attributeState,
  }),
  replaceAttributeStates: (attributesState: Record<string, BulkAttributeState>): Action => ({
    type: Events.REPLACE_ATTRIBUTE_STATES,
    attributesState,
  }),
  prepareConfirmation: (payload: {
    validatedStates: Record<string, BulkAttributeState>;
    updates: Array<{ attrInternalName: string; value: BulkAttributeValue }>;
  }): Action => ({
    type: Events.PREPARE_CONFIRMATION,
    validatedStates: payload.validatedStates,
    updates: payload.updates,
  }),
  toggleCollapsedGroup: (groupId: number): Action => ({
    type: Events.TOGGLE_COLLAPSED_GROUP,
    groupId,
  }),
  setConfirmOpen: (open: boolean): Action => ({ type: Events.SET_CONFIRM_OPEN, open }),
  setSubmitting: (isSubmitting: boolean): Action => ({
    type: Events.SET_SUBMITTING,
    isSubmitting,
  }),
  setHasAttemptedSave: (hasAttemptedSave: boolean): Action => ({
    type: Events.SET_HAS_ATTEMPTED_SAVE,
    hasAttemptedSave,
  }),
  clearAll: (attributesState: Record<string, BulkAttributeState>): Action => ({
    type: Events.CLEAR_ALL,
    attributesState,
  }),
};

export const createInitialBulkUpdateDialogState = (): BulkUpdateDialogState => ({
  search: '',
  attributesState: {},
  collapsedGroups: [],
  pendingUpdates: [],
  isConfirmOpen: false,
  isSubmitting: false,
  hasAttemptedSave: false,
});

export function bulkUpdateDialogReducer(state: BulkUpdateDialogState, action: Action) {
  return produce(state, (draft) => {
    switch (action.type) {
      case Events.RESET_DIALOG: {
        draft.attributesState = action.attributesState;
        draft.search = '';
        draft.collapsedGroups = action.collapsedGroups;
        draft.pendingUpdates = [];
        draft.isConfirmOpen = false;
        draft.isSubmitting = false;
        draft.hasAttemptedSave = false;
        return;
      }
      case Events.SET_SEARCH: {
        draft.search = action.search;
        return;
      }
      case Events.UPSERT_ATTRIBUTE_STATE: {
        draft.attributesState[action.attrInternalName] = action.attributeState;
        return;
      }
      case Events.REPLACE_ATTRIBUTE_STATES: {
        draft.attributesState = action.attributesState;
        return;
      }
      case Events.PREPARE_CONFIRMATION: {
        draft.attributesState = action.validatedStates;
        draft.pendingUpdates = action.updates;
        draft.hasAttemptedSave = true;
        draft.isConfirmOpen = true;
        return;
      }
      case Events.TOGGLE_COLLAPSED_GROUP: {
        draft.collapsedGroups = draft.collapsedGroups.map((item) =>
          item.groupId === action.groupId ? { ...item, collapsed: !item.collapsed } : item,
        );
        return;
      }
      case Events.SET_CONFIRM_OPEN: {
        draft.isConfirmOpen = action.open;
        return;
      }
      case Events.SET_SUBMITTING: {
        draft.isSubmitting = action.isSubmitting;
        return;
      }
      case Events.SET_HAS_ATTEMPTED_SAVE: {
        draft.hasAttemptedSave = action.hasAttemptedSave;
        return;
      }
      case Events.CLEAR_ALL: {
        draft.attributesState = action.attributesState;
        draft.pendingUpdates = [];
        draft.hasAttemptedSave = false;
        return;
      }
      default:
        return state;
    }
  });
}
